/**
 * HEIC → JPEG conversion. iOS Safari still emits HEIC by default for
 * uploaded photos; the Worker-side Gemma multimodal extractor expects a
 * format it can actually parse, so we convert in the browser before POST.
 *
 * `heic2any` is lazy-loaded so the ~1MB HEIC parser only ships when
 * someone actually uploads a HEIC — the common JPG/PNG path skips it.
 */

const HEIC_MIME_TYPES = new Set([
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
]);

function hasHeicExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.endsWith('.heic') || lower.endsWith('.heif');
}

export function isHeic(file: File): boolean {
  return HEIC_MIME_TYPES.has(file.type) || hasHeicExtension(file.name);
}

/** Convert a HEIC/HEIF file to JPEG, returning a new File. Non-HEIC inputs
 *  are returned untouched. Throws if conversion fails. */
export async function heicToJpeg(file: File, quality = 0.85): Promise<File> {
  if (!isHeic(file)) return file;

  // Lazy-load to keep the welcome/chat bundle tiny.
  const heic2anyModule = await import('heic2any');
  const heic2any = heic2anyModule.default;

  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality,
  });

  // heic2any returns Blob for single images or Blob[] for multi-frame HEIC.
  // Flatten to a single blob by taking the first frame.
  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) throw new Error('HEIC conversion returned no output');

  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([blob], newName, {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}

/** Read a File as a base64-encoded string. Strips the `data:<mime>;base64,`
 *  prefix so the Worker gets raw base64 it can hand to the LLM. */
export async function fileToBase64(file: File): Promise<string> {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader returned non-string result'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
